/**
 * 激活转化看板 adapter（ADM-MTR-S013）：GET /api/v1/metrics/activation-conversion 的 snake_case DTO
 * → 页面消费的 camelCase 行，并派生比例 / 转化率 / 占比（分母为 0 → null，页面显示 —）。
 *
 * 派生口径（dev-docs AD §15.3）：
 * - 比例 = 各级人数 ÷ 激活人数
 * - 转化率 = 本级 ÷ 上一级（进付费章 ÷ 进广告小说、进付费面板 ÷ 进付费章、下单 ÷ 进付费面板、
 *   付费 ÷ 下单）；各级嵌套，不超过 100%
 * - 占比 = 本行人数 ÷ 汇总行人数
 */

import type {
  ActivationConversionData,
  ActivationConversionDimension,
  ActivationConversionMetrics,
} from '@novel-admin/shared';
import { ratio } from './metrics-paywall-conversion-adapter';

export type ActivationConversionMetricsView = {
  activations: number;
  adNovelUsers: number;
  paidChapterUsers: number;
  paywallUsers: number;
  orderUsers: number;
  paidUsers: number;
  adNovelRate: number | null;
  paidChapterRate: number | null;
  paywallRate: number | null;
  orderRate: number | null;
  paidRate: number | null;
  paidChapterConversion: number | null;
  paywallConversion: number | null;
  orderConversion: number | null;
  paidConversion: number | null;
  activationsShare: number | null;
  adNovelShare: number | null;
  paidChapterShare: number | null;
  paywallShare: number | null;
  orderShare: number | null;
  paidShare: number | null;
};

export type ActivationConversionRowView = ActivationConversionMetricsView & {
  key: string;
  dims: Partial<Record<ActivationConversionDimension, string | null>>;
};

export type ActivationConversionViewData = {
  window: { from: string; to: string };
  observedUntil: string;
  groupBy: ActivationConversionDimension[];
  summary: ActivationConversionMetricsView;
  rows: ActivationConversionRowView[];
  truncated: boolean;
  options: ActivationConversionData['options'];
  generatedAt: string;
};

function toView(
  metrics: ActivationConversionMetrics,
  summary: ActivationConversionMetrics,
): ActivationConversionMetricsView {
  const m = metrics;
  return {
    activations: m.activations,
    adNovelUsers: m.ad_novel_users,
    paidChapterUsers: m.paid_chapter_users,
    paywallUsers: m.paywall_users,
    orderUsers: m.order_users,
    paidUsers: m.paid_users,
    adNovelRate: ratio(m.ad_novel_users, m.activations),
    paidChapterRate: ratio(m.paid_chapter_users, m.activations),
    paywallRate: ratio(m.paywall_users, m.activations),
    orderRate: ratio(m.order_users, m.activations),
    paidRate: ratio(m.paid_users, m.activations),
    paidChapterConversion: ratio(m.paid_chapter_users, m.ad_novel_users),
    paywallConversion: ratio(m.paywall_users, m.paid_chapter_users),
    orderConversion: ratio(m.order_users, m.paywall_users),
    paidConversion: ratio(m.paid_users, m.order_users),
    activationsShare: ratio(m.activations, summary.activations),
    adNovelShare: ratio(m.ad_novel_users, summary.ad_novel_users),
    paidChapterShare: ratio(m.paid_chapter_users, summary.paid_chapter_users),
    paywallShare: ratio(m.paywall_users, summary.paywall_users),
    orderShare: ratio(m.order_users, summary.order_users),
    paidShare: ratio(m.paid_users, summary.paid_users),
  };
}

export function adaptMetricsActivationConversion(
  dto: ActivationConversionData,
): ActivationConversionViewData {
  return {
    window: { from: dto.window.from, to: dto.window.to },
    observedUntil: dto.observed_until,
    groupBy: dto.group_by,
    summary: toView(dto.summary, dto.summary),
    rows: dto.rows.map((row) => ({
      // 维度值是自由文本（可含任意分隔符），整组 JSON 序列化才能保证 key 唯一且 null 不与字面值相撞
      key: JSON.stringify(dto.group_by.map((dimension) => row.dims[dimension] ?? null)),
      dims: row.dims,
      ...toView(row, dto.summary),
    })),
    truncated: dto.truncated,
    options: dto.options,
    generatedAt: dto.generated_at,
  };
}
